/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { shortenandHoverString, showBigImage, hideImage, getImageFullUrlBasedonPath } from '../../../../../Components/helper';
import noImage from '../../../../../assets/images/no-image.png';
import CustomDatePicker from '../../../../../Components/Utility/DatePicker/DatePicker';
import StatusCell from '../../../../../Components/StatusCell/StatusCell';

function EditableFieldContainer({ children }) {
  return <div className="editable-field-container">{children}</div>;
}

EditableFieldContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

EditableFieldContainer.displayName = 'EditableFieldContainer';

function TableBody({
  state,
  isColVisible,
  handleSelectPO,
  expandPO,
  renderGroupCells,
  isRowEditable,
  isColumnEditable,
  handleAutoSave,
  setState,
  POAddEditAccess,
  currencySymbol,
  lengthVisible,
  t,
}) {
  const selectDatePlaceholder = useMemo(() => t('CREATE_EDIT_PO.SELECT_DATE'), [t]);
  const history = useHistory();

  const createExpandedLineObject = useCallback((order, expandRow) => {
    const detail = expandRow?.orderDetails?.[0] || {};
    return {
      ...expandRow,
      purchaseOrderId: order._id,
      orderDetailsId: detail?._id || expandRow?.orderDetailsId,
      _id: expandRow._id || `${order._id}-expand-${detail?._id || expandRow?.orderDetailsId}`,
      parentPackType: order.packType,
      parentRowId: expandRow.parentRowId,
    };
  }, []);

  const handleEditPO = useCallback(
    (order) => {
      const poId = order._id;
      let devOrderId = null;

      if (order.orderDetails && order.orderDetails.length > 0) {
        const firstOrderDetail = order.orderDetails[0];
        if (firstOrderDetail.productNumber) {
          if (typeof firstOrderDetail.productNumber === 'object' && firstOrderDetail.productNumber._id) {
            devOrderId = firstOrderDetail.productNumber._id;
          } else if (typeof firstOrderDetail.productNumber === 'string') {
            devOrderId = firstOrderDetail.productNumber;
          }
        }
      }

      if (!devOrderId || !poId) {
        return;
      }

      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem('todoReturnPath', currentPath);
      let editPath = `/development-order/${devOrderId}/product-purchase-order/edit/${poId}`;
      const queryParams = new URLSearchParams();
      if (order.orderDetails && order.orderDetails.length > 0) {
        const firstOrderDetail = order.orderDetails[0];
        if (firstOrderDetail.productNumber) {
          if (typeof firstOrderDetail.productNumber === 'object' && firstOrderDetail.productNumber.number) {
            queryParams.set('productNumber', firstOrderDetail.productNumber.number);
          } else if (typeof firstOrderDetail.productNumber === 'string') {
            queryParams.set('productNumber', firstOrderDetail.productNumber);
          }
        }

        if (firstOrderDetail.colourway) {
          if (typeof firstOrderDetail.colourway === 'object' && firstOrderDetail.colourway.name) {
            queryParams.set('colourway', firstOrderDetail.colourway.name);
          } else if (typeof firstOrderDetail.colourway === 'string') {
            queryParams.set('colourway', firstOrderDetail.colourway);
          }
        }

        if (firstOrderDetail.size) {
          if (typeof firstOrderDetail.size === 'object' && firstOrderDetail.size.name) {
            queryParams.set('size', firstOrderDetail.size.name);
          } else if (typeof firstOrderDetail.size === 'string') {
            queryParams.set('size', firstOrderDetail.size);
          }
        }

        if (firstOrderDetail.length) {
          if (typeof firstOrderDetail.length === 'object' && firstOrderDetail.length.name) {
            queryParams.set('length', firstOrderDetail.length.name);
          } else if (typeof firstOrderDetail.length === 'string') {
            queryParams.set('length', firstOrderDetail.length);
          }
        }
      }

      queryParams.set('fromTodo', 'true');

      if (queryParams.toString()) {
        editPath += `?${queryParams.toString()}`;
      }

      window.open(editPath, '_blank', 'noopener,noreferrer');
    },
    [history],
  );

  const getImageSrc = useCallback((order) => {
    const smallImage = order?.image ?? order?.companyLogo ?? order?.originalPOImage;
    if (!smallImage || (typeof smallImage === 'string' && !smallImage.trim())) {
      return noImage;
    }
    return getImageFullUrlBasedonPath(smallImage) || noImage;
  }, []);

  const getLargeImageSrc = useCallback((order) => {
    const largeImage = order?.image ?? order?.companyLogo ?? order?.originalPOImage;
    if (!largeImage || (typeof largeImage === 'string' && !largeImage.trim())) {
      return noImage;
    }
    return getImageFullUrlBasedonPath(largeImage) || noImage;
  }, []);

  const formatDate = useCallback((date) => {
    if (!date) return '-';
    return moment(date).format('DD-MMM-YYYY');
  }, []);

  const formatCurrency = useCallback(
    (amount) => {
      if (!amount && amount !== 0) return `0.00 ${currencySymbol}`;
      return `${parseFloat(amount).toFixed(2)} ${currencySymbol}`;
    },
    [currencySymbol],
  );

  const formatNumber = useCallback((number) => {
    if (!number && number !== 0) return '0';
    return parseFloat(number).toFixed(2);
  }, []);

  const formatDiscountPercentage = useCallback((percentage) => {
    if (percentage === null || percentage === undefined || percentage === '') return '-';
    if (percentage === 0) return '0%';
    return `${parseFloat(percentage).toFixed(2)}%`;
  }, []);

  const updateChildRowState = useCallback(
    (parentOrderId, orderDetailsId, columnKey, newValue) => {
      setState((prev) => {
        const updatedOrders = prev.purchaseOrderTodos.map((o) => {
          if (o._id === parentOrderId && Array.isArray(o.expandData)) {
            return {
              ...o,
              isExpanded: o.isExpanded,
              expandData: o.expandData.map((expandRow) => {
                const detail = expandRow?.orderDetails?.[0] || {};
                const expandDetailId = detail?._id || expandRow?.orderDetailsId;
                if (expandDetailId === orderDetailsId || expandDetailId?.toString() === orderDetailsId?.toString()) {
                  return {
                    ...expandRow,
                    [columnKey]: newValue,
                    orderDetails:
                      expandRow.orderDetails?.map((od) => {
                        if (od._id === orderDetailsId || od._id?.toString() === orderDetailsId?.toString()) {
                          return { ...od, [columnKey]: newValue };
                        }
                        return od;
                      }) || expandRow.orderDetails,
                  };
                }
                return expandRow;
              }),
            };
          }
          return o;
        });
        return { ...prev, purchaseOrderTodos: updatedOrders };
      });
    },
    [setState],
  );

  const updateParentRowState = useCallback(
    (parentOrderId, columnKey, newValue) => {
      setState((prev) => ({
        ...prev,
        purchaseOrderTodos: prev.purchaseOrderTodos.map((o) => (o._id === parentOrderId ? { ...o, [columnKey]: newValue } : o)),
      }));
    },
    [setState],
  );

  const renderEditableField = useCallback(
    (order, columnKey, value, type = 'text', placeholder = '') => {
      const isEditable = isRowEditable(order) && isColumnEditable(columnKey);
      const fieldId = `${order._id}-${columnKey}`;
      const parentOrderId = order.purchaseOrderId || order._id;
      const orderDetailsId = order.orderDetailsId || order.orderDetails?.[0]?._id;
      const isChildRow = !!orderDetailsId && order.purchaseOrderId;

      const handleValueChange = (newValue) => {
        if (isChildRow && orderDetailsId) {
          updateChildRowState(parentOrderId, orderDetailsId, columnKey, newValue);
        } else {
          updateParentRowState(parentOrderId, columnKey, newValue);
        }
        handleAutoSave(order, columnKey, newValue);
      };

      if (type === 'date') {
        const handleDateChange = (date) => {
          const formattedDate = date ? new Date(date).toISOString() : '';
          handleValueChange(formattedDate);
          handleAutoSave(order, columnKey, formattedDate);
        };

        return (
          <CustomDatePicker
            portalId="root-portal"
            dateFormat="dd-MMM-yyyy"
            name={columnKey}
            popperPlace="bottom-end"
            keyProp={fieldId}
            disable={!isEditable}
            disableCalendar={!isEditable}
            onChange={handleDateChange}
            valueProp={value ? new Date(value) : null}
            placeholder={placeholder}
          />
        );
      }

      if (type === 'number') {
        return (
          <input
            type="number"
            className="form-control editable-field"
            value={value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            disabled={!isEditable}
            placeholder={placeholder}
            min="0"
            step="0.01"
          />
        );
      }

      return (
        <input
          type="text"
          className="form-control editable-field"
          value={value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          disabled={!isEditable}
          placeholder={placeholder}
        />
      );
    },
    [isRowEditable, isColumnEditable, handleAutoSave, updateChildRowState, updateParentRowState],
  );

  const renderActualTotalUnits = useCallback(
    (o) =>
      React.createElement(
        EditableFieldContainer,
        null,
        renderEditableField(o, 'actualTotalUnits', o.actualTotalUnits, 'number', t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.PLACEHOLDERS.ACTUAL_UNITS')),
      ),
    [renderEditableField, t],
  );

  const renderActualApprovedByDate = useCallback(
    (o) =>
      React.createElement(EditableFieldContainer, null, renderEditableField(o, 'actualApprovedByDate', o.actualApprovedByDate, 'date', selectDatePlaceholder)),
    [renderEditableField, t],
  );

  const renderActualExFactoryDate = useCallback(
    (o) =>
      React.createElement(EditableFieldContainer, null, renderEditableField(o, 'actualExFactoryDate', o.actualExFactoryDate, 'date', selectDatePlaceholder)),
    [renderEditableField, t],
  );

  const renderActualShipmentDate = useCallback(
    (o) => React.createElement(EditableFieldContainer, null, renderEditableField(o, 'actualShipmentDate', o.actualShipmentDate, 'date', selectDatePlaceholder)),
    [renderEditableField, t],
  );

  const renderExpandedActualApprovedByDate = useCallback(
    (o) => {
      if (o.parentRowId) {
        return '-';
      }
      return React.createElement(
        EditableFieldContainer,
        null,
        renderEditableField(o, 'actualApprovedByDate', o.actualApprovedByDate, 'date', selectDatePlaceholder),
      );
    },
    [renderEditableField, t],
  );

  const renderExpandedActualExFactoryDate = useCallback(
    (o) => {
      if (o.parentRowId) {
        return '-';
      }
      return React.createElement(
        EditableFieldContainer,
        null,
        renderEditableField(o, 'actualExFactoryDate', o.actualExFactoryDate, 'date', selectDatePlaceholder),
      );
    },
    [renderEditableField, t],
  );

  const renderExpandedActualShipmentDate = useCallback(
    (o) => {
      if (o.parentRowId) {
        return '-';
      }
      return React.createElement(
        EditableFieldContainer,
        null,
        renderEditableField(o, 'actualShipmentDate', o.actualShipmentDate, 'date', selectDatePlaceholder),
      );
    },
    [renderEditableField, t],
  );

  const cellRenderers = useMemo(
    () => ({
      totalUnits: {
        original: (o) => o.totalUnits,
        revised: (o) => o.revisedTotalUnits,
        actual: renderActualTotalUnits,
      },
      totalPrice: {
        original: (o) => formatCurrency(o.totalPrice),
        revised: (o) => formatCurrency(o.revisedTotalPrice),
        actual: (o) => formatCurrency(o.actualTotalPrice),
      },
      approvedByDate: {
        original: (o) => formatDate(o.approvedByDate),
        revised: (o) => formatDate(o.revisedApprovedByDate),
        actual: renderActualApprovedByDate,
      },
      exFactoryDate: {
        original: (o) => formatDate(o.exFactoryDate),
        revised: (o) => formatDate(o.revisedExFactoryDate),
        actual: renderActualExFactoryDate,
      },
      shipmentDate: {
        original: (o) => formatDate(o.shipmentDate),
        revised: (o) => formatDate(o.revisedShipmentDate),
        actual: renderActualShipmentDate,
      },
    }),
    [renderActualTotalUnits, renderActualApprovedByDate, renderActualExFactoryDate, renderActualShipmentDate, formatCurrency, formatDate],
  );

  const expandedCellRenderers = useMemo(
    () => ({
      totalUnits: {
        original: (o) => formatNumber(o.totalUnits),
        revised: (o) => formatNumber(o.revisedTotalUnits),
        actual: renderActualTotalUnits,
      },
      totalPrice: {
        original: (o) => formatCurrency(o.totalPrice),
        revised: (o) => formatCurrency(o.revisedTotalPrice),
        actual: (o) => formatCurrency(o.actualTotalPrice),
      },
      approvedByDate: {
        original: (o) => formatDate(o.approvedByDate),
        revised: (o) => {
          if (o.parentRowId) {
            return '-';
          }
          return formatDate(o.revisedApprovedByDate);
        },
        actual: renderExpandedActualApprovedByDate,
      },
      exFactoryDate: {
        original: (o) => formatDate(o.exFactoryDate),
        revised: (o) => {
          if (o.parentRowId) {
            return '-';
          }
          return formatDate(o.revisedExFactoryDate);
        },
        actual: renderExpandedActualExFactoryDate,
      },
      shipmentDate: {
        original: (o) => formatDate(o.shipmentDate),
        revised: (o) => {
          if (o.parentRowId) {
            return '-';
          }
          return formatDate(o.revisedShipmentDate);
        },
        actual: renderExpandedActualShipmentDate,
      },
    }),
    [
      renderActualTotalUnits,
      renderExpandedActualApprovedByDate,
      renderExpandedActualExFactoryDate,
      renderExpandedActualShipmentDate,
      formatCurrency,
      formatDate,
      formatNumber,
    ],
  );

  const renderExpandButton = useCallback(
    (order) => {
      const hasExpandableData = Array.isArray(order?.expandData) && order.expandData.length > 0;

      if (!hasExpandableData) return null;

      return (
        <button
          type="button"
          className="btn-up-down product-purchase-order-todo-expand-btn"
          onClick={() => expandPO(order._id)}
          aria-label={order.isExpanded ? t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.COLLAPSE') : t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.EXPAND')}
        >
          <span className={`icon-new-down-arrow-icon ${order.isExpanded ? 'icon-inverse' : ''}`} />
        </button>
      );
    },
    [expandPO, t],
  );

  const renderTableRow = useCallback(
    (order, index) => {
      return (
        <tr key={order._id} className={order.isExpanded ? 'expanded-row' : ''}>
          <td>
            <div className="common-checkbox margin-auto">
              <input
                type="checkbox"
                onChange={handleSelectPO}
                name={`check_order_${index}`}
                id={`check_order_${index}`}
                value={order._id}
                checked={state.selectedIds.includes(order._id)}
              />
              <label htmlFor={`check_order_${index}`} className="chklabel" />
            </div>
          </td>

          <td>{renderExpandButton(order)}</td>

          <td>
            <div className="image-detail">
              <img
                className="product-purchase-order-todo-image cursor-pointer"
                src={getImageSrc(order)}
                alt="Product"
                onError={(e) => {
                  e.target.src = noImage;
                }}
                onMouseEnter={(e) => {
                  const tempImg = document.createElement('img');
                  tempImg.src = getLargeImageSrc(order);
                  tempImg.onload = () => {
                    e.target.src = getLargeImageSrc(order);
                    showBigImage(e);
                  };
                }}
                onMouseLeave={(e) => {
                  hideImage();
                  e.target.src = getImageSrc(order);
                }}
              />
            </div>
          </td>
          <td className="first-fix-col">
            <div className="d-flex align-items-center justify-content-between">
              <div className="flex-grow-1">{order.poRef ? <span>{shortenandHoverString(order.poRef, 40)}</span> : '-'}</div>
              {POAddEditAccess && order.poRef && (
                <button
                  type="button"
                  className="btn btn-tbl-action p-0 ml-2"
                  onClick={() => handleEditPO(order)}
                  title={t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.EDIT_PO')}
                  aria-label={t('PURCHASE_ORDER.PRODUCT_PURCHASE_ORDER_TODO.EDIT_PO')}
                >
                  <span className="icon-Edit-1" />
                </button>
              )}
            </div>
          </td>
          {isColVisible('supplier') && (
            <td>
              <span>{shortenandHoverString(order.supplier || '-', 40)}</span>
            </td>
          )}
          {isColVisible('factory') && (
            <td>
              <span>{shortenandHoverString(order.factory || '-', 40)}</span>
            </td>
          )}
          {isColVisible('templateName') && (
            <td>
              <span>{shortenandHoverString(order.templateName || '-', 40)}</span>
            </td>
          )}
          {isColVisible('packType') && <td>{order.packType || '-'}</td>}
          {isColVisible('productNumber') && (
            <td>
              <span>{shortenandHoverString(order.productNumber || '-', 40)}</span>
            </td>
          )}
          {isColVisible('brand') && <td>{order.brand || '-'}</td>}
          {isColVisible('season') && <td>{order.season || '-'}</td>}
          {isColVisible('month') && <td>{order.month || '-'}</td>}
          {isColVisible('year') && <td>{order.year || '-'}</td>}
          {isColVisible('category1') && <td>{order.category1 || '-'}</td>}
          {isColVisible('category2') && <td>{order.category2 || '-'}</td>}
          {isColVisible('category3') && <td>{order.category3 || '-'}</td>}
          {isColVisible('category4') && <td>{order.category4 || '-'}</td>}
          {isColVisible('colourway') && (
            <td>
              <span>{shortenandHoverString(order.colourway || '-', 40)}</span>
            </td>
          )}
          {isColVisible('size') && <td>{order.size || '-'}</td>}
          {isColVisible('length') && lengthVisible && <td>{order.length || '-'}</td>}
          {isColVisible('skuNumber') && <td>{shortenandHoverString(order.skuNumber || '', 40)}</td>}
          {isColVisible('location') && <td>{shortenandHoverString(order.location || '-', 40)}</td>}
          {isColVisible('packRatio') && <td>{order.packRatio}</td>}
          {isColVisible('totalPacks') && <td>{order.totalPacks}</td>}
          {isColVisible('totalUnits') && renderGroupCells(order, 'totalUnits', cellRenderers.totalUnits, {}, { hasActual: true })}
          {isColVisible('unitPrice') && <td>{formatCurrency(order.unitPrice)}</td>}
          {isColVisible('discountPct') && <td>{formatDiscountPercentage(order.discountPct)}</td>}
          {isColVisible('priceAfterDiscount') && <td>{formatCurrency(order.priceAfterDiscount)}</td>}
          {isColVisible('totalPrice') && renderGroupCells(order, 'totalPrice', cellRenderers.totalPrice, {}, { hasActual: true })}
          {isColVisible('approvedByDate') && renderGroupCells(order, 'approvedByDate', cellRenderers.approvedByDate, {}, { hasActual: true })}
          {isColVisible('exFactoryDate') && renderGroupCells(order, 'exFactoryDate', cellRenderers.exFactoryDate, {}, { hasActual: true })}
          {isColVisible('shipmentDate') && renderGroupCells(order, 'shipmentDate', cellRenderers.shipmentDate, {}, { hasActual: true })}
          {isColVisible('deliveryDate') && <td>{formatDate(order.deliveryDate)}</td>}
          <td className="last-fix-col">
            <StatusCell status={order.status} t={t} />
          </td>
        </tr>
      );
    },
    [
      state.selectedIds,
      handleSelectPO,
      renderExpandButton,
      getImageSrc,
      getLargeImageSrc,
      isColVisible,
      lengthVisible,
      renderGroupCells,
      renderEditableField,
      formatDate,
      formatCurrency,
      formatNumber,
      formatDiscountPercentage,
      handleEditPO,
      POAddEditAccess,
      t,
    ],
  );

  const renderExpandedContent = useCallback(
    (order) => {
      if (!order.isExpanded || !order.expandData || order.expandData.length === 0) {
        return null;
      }

      return order.expandData.map((expandItem, expandIndex) => {
        const line = createExpandedLineObject(order, expandItem);
        return (
          <tr key={`${order._id}-expand-${expandIndex}`} className="product-purchase-order-todo-expanded-content">
            <td />
            <td />

            <td>
              <div className="image-detail">
                <img
                  className="product-purchase-order-todo-image cursor-pointer"
                  src={getImageSrc(expandItem)}
                  alt="Expand Product"
                  onError={(e) => {
                    e.target.src = noImage;
                  }}
                  onMouseEnter={(e) => {
                    const tempImg = document.createElement('img');
                    tempImg.src = getLargeImageSrc(expandItem);
                    tempImg.onload = () => {
                      e.target.src = getLargeImageSrc(expandItem);
                      showBigImage(e);
                    };
                  }}
                  onMouseLeave={(e) => {
                    hideImage();
                    e.target.src = getImageSrc(expandItem);
                  }}
                />
              </div>
            </td>

            <td className="first-fix-col">
              <div className="d-flex align-items-center justify-content-between">{expandItem.poRef || '-'}</div>
            </td>

            {isColVisible('supplier') && (
              <td>
                <span>{shortenandHoverString(expandItem.supplier || '-', 20)}</span>
              </td>
            )}

            {isColVisible('factory') && (
              <td>
                <span>{shortenandHoverString(expandItem.factory || '-', 20)}</span>
              </td>
            )}

            {isColVisible('templateName') && (
              <td>
                <span>{shortenandHoverString(expandItem.templateName || '-', 20)}</span>
              </td>
            )}

            {isColVisible('packType') && <td>{expandItem.packType || '-'}</td>}

            {isColVisible('productNumber') && (
              <td>
                <span>{shortenandHoverString(expandItem.productNumber || '-', 25)}</span>
              </td>
            )}

            {isColVisible('brand') && <td>{expandItem.brand || '-'}</td>}
            {isColVisible('season') && <td>{expandItem.season || '-'}</td>}
            {isColVisible('month') && <td>{expandItem.month || '-'}</td>}
            {isColVisible('year') && <td>{expandItem.year || '-'}</td>}
            {isColVisible('category1') && <td>{expandItem.category1 || '-'}</td>}
            {isColVisible('category2') && <td>{expandItem.category2 || '-'}</td>}
            {isColVisible('category3') && <td>{expandItem.category3 || '-'}</td>}
            {isColVisible('category4') && <td>{expandItem.category4 || '-'}</td>}

            {isColVisible('colourway') && (
              <td>
                <span>{shortenandHoverString(expandItem.colourway || '-', 15)}</span>
              </td>
            )}

            {isColVisible('size') && <td>{expandItem.size || '-'}</td>}
            {isColVisible('length') && lengthVisible && <td>{expandItem.length || '-'}</td>}
            {isColVisible('skuNumber') && <td>{expandItem.skuNumber || ''}</td>}
            {isColVisible('location') && <td>{expandItem.location || '-'}</td>}
            {isColVisible('packRatio') && <td>{formatNumber(expandItem.packRatio)}</td>}
            {isColVisible('totalPacks') && <td>{line?.parentRowId ? '-' : formatNumber(expandItem.totalPacks)}</td>}
            {isColVisible('totalUnits') && renderGroupCells(line, 'totalUnits', expandedCellRenderers.totalUnits, {}, { hasActual: true })}
            {isColVisible('unitPrice') && <td>{formatCurrency(expandItem.unitPrice)}</td>}
            {isColVisible('discountPct') && <td>{formatDiscountPercentage(expandItem.discountPct)}</td>}
            {isColVisible('priceAfterDiscount') && <td>{formatCurrency(expandItem.priceAfterDiscount)}</td>}
            {isColVisible('totalPrice') && renderGroupCells(expandItem, 'totalPrice', expandedCellRenderers.totalPrice, {}, { hasActual: true })}
            {isColVisible('approvedByDate') && renderGroupCells(line, 'approvedByDate', expandedCellRenderers.approvedByDate, {}, { hasActual: true })}
            {isColVisible('exFactoryDate') && renderGroupCells(line, 'exFactoryDate', expandedCellRenderers.exFactoryDate, {}, { hasActual: true })}
            {isColVisible('shipmentDate') && renderGroupCells(line, 'shipmentDate', expandedCellRenderers.shipmentDate, {}, { hasActual: true })}
            {isColVisible('deliveryDate') && <td>{formatDate(expandItem.deliveryDate)}</td>}

            <td className="last-fix-col">
              <StatusCell status={expandItem.status} t={t} />
            </td>
          </tr>
        );
      });
    },
    [
      getImageSrc,
      getLargeImageSrc,
      isColVisible,
      lengthVisible,
      renderGroupCells,
      renderEditableField,
      formatDate,
      formatCurrency,
      formatNumber,
      formatDiscountPercentage,
      handleEditPO,
      POAddEditAccess,
      createExpandedLineObject,
      t,
    ],
  );

  return (
    <>
      {state.purchaseOrderTodos.map((order, index) => (
        <React.Fragment key={order._id}>
          {renderTableRow(order, index)}
          {renderExpandedContent(order)}
        </React.Fragment>
      ))}
    </>
  );
}

TableBody.propTypes = {
  state: PropTypes.shape({
    purchaseOrderTodos: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string.isRequired,
        poRef: PropTypes.string,
        supplier: PropTypes.string,
        factory: PropTypes.string,
        templateName: PropTypes.string,
        productNumber: PropTypes.string,
        brand: PropTypes.string,
        season: PropTypes.string,
        month: PropTypes.string,
        year: PropTypes.string,
        category1: PropTypes.string,
        category2: PropTypes.string,
        category3: PropTypes.string,
        category4: PropTypes.string,
        colourway: PropTypes.string,
        size: PropTypes.string,
        length: PropTypes.string,
        status: PropTypes.string,
        isExpanded: PropTypes.bool,
        isOldPO: PropTypes.bool,
      }),
    ).isRequired,
    selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  isColVisible: PropTypes.func.isRequired,
  handleSelectPO: PropTypes.func.isRequired,
  expandPO: PropTypes.func.isRequired,
  renderGroupCells: PropTypes.func.isRequired,
  isRowEditable: PropTypes.func.isRequired,
  isColumnEditable: PropTypes.func.isRequired,
  handleAutoSave: PropTypes.func.isRequired,
  setState: PropTypes.func.isRequired,
  POAddEditAccess: PropTypes.bool.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  lengthVisible: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

export default React.memo(TableBody);
